import React, { useRef } from 'react';
import html2canvas from 'html2canvas';
import { ChevronLeft, FileText, Image, Eye } from 'lucide-react';
import StepProgressBar from '../../StepProgressBar';
import ProjectStatusTable from './ProjectStatusTable';

const ChartDetailsModal = ({
  isOpen,
  onClose,
  chartType,
  data,
  isFinancialTable,
  setIsFinancialTable,
  selectedShip,
  setSelectedShip,
  projectStatusApiData
}) => {
  const chartRef = useRef(null);

  const handleDownloadPNG = () => {
    if (!chartRef.current) return;

    html2canvas(chartRef.current).then(canvas => {
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = url;
      link.download = `${chartType}-details.png`;
      link.click();
    });
  };

  const handleDownloadCSV = () => {
    if (!data || data.length === 0) return;

    let csvContent = '';
    let headers = [];

    switch (chartType) {
      case 'sCurve':
        headers = ['Month', 'Contractual', 'Revised', 'Achieved', 'Variance'];
        csvContent = "data:text/csv;charset=utf-8," + headers.join(',') + "\n";
        csvContent += data.map(row => {
          const variance = row.achieved - row.contractual;
          return `${row.month},${row.contractual},${row.revised},${row.achieved},${variance}`;
        }).join("\n");
        break;
      case 'projectStatus':
        headers = ['Status', 'Count', 'Percentage', 'Trend'];
        csvContent = "data:text/csv;charset=utf-8," + headers.join(',') + "\n";
        csvContent += data.map(row => `${row.status},${row.count},${row.percentage},${row.trend || 0}`).join("\n");
        break;
      case 'financialOverview':
        headers = ['Category', 'Amount', 'Percentage', 'Status'];
        csvContent = "data:text/csv;charset=utf-8," + headers.join(',') + "\n";
        csvContent += data.map(row => {
          const status = row.percentage >= 80 ? 'On Track' : row.percentage >= 50 ? 'Moderate' : 'At Risk';
          return `${row.category},${row.amount},${row.percentage},${status}`;
        }).join("\n");
        break;
      default:
        return;
    }

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `${chartType}-details.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderTable = () => {
    switch (chartType) {
      case 'sCurve':
        const displayData = Array.isArray(data) ? data : [];
        function getLastNonNull(arr, key) {
          for (let i = arr.length - 1; i >= 0; i--) {
            if (arr[i][key] !== null && arr[i][key] !== undefined) {
              return arr[i][key];
            }
          }
          return 0;
        }
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
              <h4 className="text-sm font-medium text-blue-800 mb-4">S-Curve Progress Overview</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded-lg shadow flex flex-col items-center">
                  <p className="text-xs text-gray-500 mb-1">Latest Contractual</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {getLastNonNull(displayData, 'Contractual')}%
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow flex flex-col items-center">
                  <p className="text-xs text-gray-500 mb-1">Latest Revised</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {getLastNonNull(displayData, 'Revised')}%
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow flex flex-col items-center">
                  <p className="text-xs text-gray-500 mb-1">Latest Achieved</p>
                  <p className="text-2xl font-bold text-green-600">
                    {getLastNonNull(displayData, 'Achieved')}%
                  </p>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full rounded-lg overflow-hidden border border-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-6 py-3 text-left font-bold text-gray-700">Month</th>
                    <th className="px-6 py-3 text-center font-bold text-gray-700">Contractual %</th>
                    <th className="px-6 py-3 text-center font-bold text-gray-700">Revised %</th>
                    <th className="px-6 py-3 text-center font-bold text-gray-700">Achieved %</th>
                    <th className="px-6 py-3 text-center font-bold text-gray-700">Variance</th>
                    <th className="px-6 py-3 text-center font-bold text-gray-700">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {displayData.map((row, idx) => {
                    const variance = (row.Achieved ?? 0) - (row.Contractual ?? 0);
                    const status = variance >= 0 ? 'On Track' : 'Behind Schedule';
                    return (
                      <tr key={idx} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                        <td className="px-6 py-3">{row.month} {row.year}</td>
                        <td className="px-6 py-3 text-center">{row.Contractual ?? '-'}</td>
                        <td className="px-6 py-3 text-center">{row.Revised ?? '-'}</td>
                        <td className="px-6 py-3 text-center">{row.Achieved ?? '-'}</td>
                        <td className="px-6 py-3 text-center">{variance > 0 ? '+' : ''}{variance}</td>
                        <td className="px-6 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold
                            ${status === 'On Track' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'projectStatus':
        return <ProjectStatusTable data={projectStatusApiData} />;

      case 'financialOverview':
        const totalBudget = data.reduce((sum, item) => sum + item.amount, 0);
        const totalSpent = data.reduce((sum, item) => sum + item.breakdown.spent, 0);
        const totalRemaining = data.reduce((sum, item) => sum + item.breakdown.remaining, 0);

        return (
          <div className="space-y-6">
            <div className="bg-green-50 p-4 rounded-lg border border-green-100">
              <h4 className="text-sm font-medium text-green-800 mb-4 flex items-center gap-2">Financial Summary</h4>
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-white p-4 rounded shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-gray-500">Total Budget</p>
                    <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">100%</span>
                  </div>
                  <p className="text-xl font-semibold text-gray-900">₹{totalBudget}Cr</p>
                  <div className="mt-2 text-xs text-gray-500">
                    Fiscal Year 2023-24
                  </div>
                </div>
                <div className="bg-white p-4 rounded shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-gray-500">Utilized</p>
                    <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">
                      {Math.round((totalSpent / totalBudget) * 100)}%
                    </span>
                  </div>
                  <p className="text-xl font-semibold text-green-600">₹{totalSpent}Cr</p>
                  <div className="mt-2 text-xs text-gray-500">
                    Updated {new Date().toLocaleDateString()}
                  </div>
                </div>
                <div className="bg-white p-4 rounded shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-gray-500">Remaining</p>
                    <span className="text-xs px-2 py-1 bg-orange-100 text-orange-800 rounded-full">
                      {Math.round((totalRemaining / totalBudget) * 100)}%
                    </span>
                  </div>
                  <p className="text-xl font-semibold text-orange-600">₹{totalRemaining}Cr</p>
                  <div className="mt-2 text-xs text-gray-500">
                    Available Balance
                  </div>
                </div>
                <div className="bg-white p-4 rounded shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-gray-500">Next Stage Payment</p>
                    <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full">
                      14%
                    </span>
                  </div>
                  <p className="text-xl font-semibold text-yellow-600">₹280Cr</p>
                  <div className="mt-2 text-xs text-gray-500">
                    Next Stage Payment : <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full border border-yellow-200 glow-animation">Stage 3</span>
                  </div>
                </div>
              </div>
            </div>
            {!isFinancialTable && (
            <StepProgressBar 
              steps={[
                { label: "Request Submitted" },
                { label: "Received Responses" },
                { label: "Negotiation Done" },
                { label: "Hired Professional" },
                { label: "Service Completed" },
              ]} 
              currentStep={3} 
              onClick={() => {setIsFinancialTable(true)}} 
            />
            )}
            {isFinancialTable && (
            <div className="overflow-x-auto">
              <button className="text-sm bg-blue-100 text-gray-700 flex gap-2 items-center pe-3 py-1 rounded hover:bg-blue-300 transition-colors" onClick={() => {setIsFinancialTable(false)}}>
                <ChevronLeft size={20}/> Back
              </button>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Allocated</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Spent</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remaining</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">% of Total</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.map((row, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className={`h-3 w-3 rounded-full mr-2 ${row.status === 'On Track' ? 'bg-green-500' :
                              row.status === 'At Risk' ? 'bg-red-500' :
                                'bg-yellow-500'
                            }`}></div>
                          <span className="text-sm font-medium text-gray-900">{row.category}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ₹{row.breakdown.allocated}Cr
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ₹{row.breakdown.spent}Cr
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ₹{row.breakdown.remaining}Cr
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                            <div
                              className={`h-2 rounded-full ${row.status === 'On Track' ? 'bg-green-500' :
                                  row.status === 'At Risk' ? 'bg-red-500' :
                                    'bg-yellow-500'
                                }`}
                              style={{ width: `${row.percentage}%` }}
                            ></div>
                          </div>
                          <span className="text-sm text-gray-900">{row.percentage}%</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${row.status === 'On Track' ? 'bg-green-100 text-green-800' :
                            row.status === 'At Risk' ? 'bg-red-100 text-red-800' :
                              'bg-yellow-100 text-yellow-800'
                          }`}>
                          {row.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </div>
        );

      case 'criticalPath':
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
              <h4 className="text-sm font-medium text-blue-800 mb-2">Critical Path Overview</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white p-3 rounded shadow-sm">
                  <p className="text-xs text-gray-500">Total Tasks</p>
                  <p className="text-lg font-semibold text-blue-600">{data.length}</p>
                </div>
                <div className="bg-white p-3 rounded shadow-sm">
                  <p className="text-xs text-gray-500">Completed</p>
                  <p className="text-lg font-semibold text-green-600">
                    {data.filter(task => task.status === 'completed').length}
                  </p>
                </div>
                <div className="bg-white p-3 rounded shadow-sm">
                  <p className="text-xs text-gray-500">In Progress</p>
                  <p className="text-lg font-semibold text-yellow-600">
                    {data.filter(task => task.status === 'in-progress').length}
                  </p>
                </div>
                <div className="bg-white p-3 rounded shadow-sm">
                  <p className="text-xs text-gray-500">Pending</p>
                  <p className="text-lg font-semibold text-red-600">
                    {data.filter(task => task.status === 'pending').length}
                  </p>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Task</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dependencies</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.map((row, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-sm text-gray-900">{row.task}</span>
                          <span className="ml-2 text-xs text-gray-500">({row.taskId})</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${row.type === 'planning' ? 'bg-blue-100 text-blue-800' :
                            row.type === 'setup' ? 'bg-purple-100 text-purple-800' :
                              row.type === 'block' ? 'bg-orange-100 text-orange-800' :
                                row.type === 'testing' ? 'bg-green-100 text-green-800' :
                                  'bg-gray-100 text-gray-800'
                          }`}>
                          {row.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.startDate}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.endDate}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.duration}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${row.status === 'completed' ? 'bg-green-100 text-green-800' :
                            row.status === 'in-progress' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                          }`}>
                          {row.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {row.dependencies.length > 0 ? row.dependencies.join(', ') : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const getNavigationPath = () => {
    switch (chartType) {
      case 'sCurve':
        return '/scurve-data';
      case 'projectStatus':
        return '/masters/project';
      case 'criticalPath':
        return '/pertchart';
      case 'financialOverview':
        return '/financemanagement';
      default:
        return '/';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-11/12 max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b flex justify-between items-center bg-gray-50">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {chartType === 'sCurve' ? 'S-Curve Details' :
              chartType === 'projectStatus' ? 'Project Status Details' :
              chartType === 'criticalPath' ? 'Critical Path Details' :
              chartType === 'financialOverview' ? 'Financial Overview Details' : 'Chart Details'}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {chartType === 'sCurve' ? 'Detailed progress tracking and analysis' :
              chartType === 'projectStatus' ? 'Comprehensive project status overview' :
              chartType === 'criticalPath' ? 'Critical path analysis and dependencies' :
              chartType === 'financialOverview' ? 'Financial performance and budget analysis' : ''}
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <button
                onClick={handleDownloadCSV}
                className="px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center space-x-2 transition-colors"
                title="Download CSV"
              >
                <FileText size={16} />
                <span>CSV</span>
              </button>
              <button
                onClick={handleDownloadPNG}
                className="px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center space-x-2 transition-colors"
                title="Download PNG"
              >
                <Image size={16} />
                <span>PNG</span>
              </button>
            </div>
            <button
              onClick={() => window.location.href = getNavigationPath()}
              className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 flex items-center space-x-2 transition-colors"
            >
              <Eye size={16} />
              <span>View Module</span>
            </button>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              ✕
            </button>
          </div>
        </div>
        <div className="p-6 overflow-auto flex-grow">
          <div ref={chartRef}>
            {renderTable()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChartDetailsModal;